import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { type RouteLocationRaw, useRoute } from 'vue-router';
import { watchImmediate } from '@vueuse/core';
import { verify } from '@/util/verify';
import type { CaseDetailsApiObjectBase, CaseNotification } from '@/api/caseSettings';
import { CaseStatus } from '@/api/caseSearch';
import { ROUTES } from '@/router';
import { enabledActionsByStatus } from '@/stores/enabledActionsByStatus';
import { asyncTimeout, logger } from '@/util';
import { type AxiosRequestConfig, type AxiosResponse, HttpStatusCode } from 'axios';
import { client } from '@/api/http';
import { AppError } from '@/stores/appErrorStore';
import { errorDetail } from '@/api/errorDetail';

export const caseCompletedMessage = 'Case processing complete, ready for planning';

const log = logger('poll-case');
const POLL_INTERVAL_MILLISECONDS = 30 * 1000;

export const useCaseSyncStore = defineStore('case-sync', () => {
    const route = useRoute();
    const case_ = ref<CaseDetailsApiObjectBase | null>(null);
    const isCaseSpecific = ref<boolean>(false);

    let cancellation: AbortController | null = null;

    function _clearState() {
        case_.value = null;
        cancellation?.abort();
    }

    async function _startCaseSync(caseId: string): Promise<void> {
        cancellation?.abort();
        cancellation = new AbortController();

        const _onFetch = (data: CaseDetailsApiObjectBase | null) => {
            if (data) {
                case_.value = data;
            } else {
                cancellation?.abort();
                case_.value = null;
            }
        };

        await _pollCase(Number(caseId), _onFetch, cancellation.signal);
    }

    const unwatch = watchImmediate(
        () => [route.meta.caseSpecific, route.params.id],
        async ([caseSpecific, caseId]) => {
            isCaseSpecific.value = !!caseSpecific;

            if (caseSpecific) {
                const id = verify(caseId as string, 'caseId should exist on case-specific routes');
                await _startCaseSync(id);
            } else {
                _clearState();
            }
        },
    );

    async function refresh() {
        if (case_.value?.id) {
            await _startCaseSync(case_.value.id.toString());
        }
    }

    const statusState = computed<CaseStatus>(() => {
        return case_.value?.status ?? CaseStatus.Error;
    });

    function caseHasStatusState(...states: CaseStatus[]): boolean {
        return states.find((s) => s === statusState.value) !== undefined;
    }

    function notificationsByStudy(study: number | null): CaseNotification[] {
        return case_.value?.notifications.filter((n) => n.study === study) ?? [];
    }

    const isCaseProcessing = computed(() =>
        caseHasStatusState(
            CaseStatus.GeneratingDicoms,
            CaseStatus.ImageSegmentation,
            CaseStatus.SurgicalTemplating,
            CaseStatus.GeneratingPlan,
        ),
    );

    const isSegmentationComplete = computed(
        () => !caseHasStatusState(CaseStatus.GeneratingDicoms, CaseStatus.ImageSegmentation),
    );

    const isCaseComplete = computed(() => caseHasStatusState(CaseStatus.Completed));

    const isCaseWarning = computed(() => caseHasStatusState(CaseStatus.Warning));

    const isCaseNew = computed(() => caseHasStatusState(CaseStatus.Created));

    const isCaseError = computed(() => caseHasStatusState(CaseStatus.Error));

    return {
        case_,
        notifications: computed<CaseNotification[]>(() => case_.value?.notifications ?? []),
        caseName: computed<string | null>(() => case_.value?.name ?? null),
        study: computed<number | null>(() => case_.value?.study ?? null),
        hipSpineStudyId: computed<number | null>(() => case_.value?.hip_spine_study_id ?? null),
        routeTo: (route: ROUTES): RouteLocationRaw | undefined => {
            if (case_.value === null) {
                return undefined;
            } else {
                const actions = enabledActionsByStatus(case_.value.status);
                return actions.includes(route)
                    ? {
                          name: route,
                          params: { id: case_.value.id },
                      }
                    : undefined;
            }
        },
        isCaseSpecific,
        isCaseComplete,
        isCaseWarning,
        isCaseProcessing,
        isSegmentationComplete,
        isCaseNew,
        isCaseError,
        statusState,
        notificationsByStudy,
        refresh,
        stop: () => {
            unwatch();
            cancellation?.abort();
        },
    };
});

async function _getCaseDetails(
    id: number,
    config?: AxiosRequestConfig,
): Promise<CaseDetailsApiObjectBase> {
    return client.get(`/cases/${id}`, config).then(({ status, data }: AxiosResponse) => {
        if (status === HttpStatusCode.Ok) {
            return data;
        } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
            throw new AppError(
                'Case not found' + errorDetail(data),
                'This case does not exist or you do not have permission to view it.',
            );
        }

        throw Error(`Failed to load case settings: ${data?.detail}`);
    });
}

/**
 * Repeatedly fetch a case until it is cancelled
 */
async function _pollCase(
    caseId: number,
    onFetch: (case_: CaseDetailsApiObjectBase | null) => void,
    signal: AbortSignal,
): Promise<void> {
    try {
        while (!signal.aborted) {
            // Attempt to fetch the case
            const case_ = (await _getCaseDetails(caseId, { signal })) ?? null;

            signal.throwIfAborted();

            onFetch(case_);

            signal.throwIfAborted();

            await asyncTimeout(POLL_INTERVAL_MILLISECONDS, signal);
        }
    } catch (e: unknown) {
        if (signal.aborted) {
            log.debug('case polling cancelled');
            return;
        }

        throw e;
    }
}
